import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InflationInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/comparison`, {
        withCredentials: true
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const hasData = !insights?.personal_inflation?.insufficient_data;
  const sampleItems = insights?.personal_inflation?.sample_items || [];

  return (
    <div className="min-h-screen bg-background" data-testid="inflation-insights">
      <Navigation />
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl mb-2">Inflation Insights</h1>
          <p className="text-muted-foreground">Deep dive into your price change patterns</p>
        </motion.div>

        {!hasData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-card border-card-border">
              <CardContent className="p-12 text-center">
                <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold text-2xl mb-2">Not Enough Data</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Add more expenses over time to see detailed insights about your personal inflation trends.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Card className="bg-card border-card-border">
                <CardHeader>
                  <CardTitle>Price Changes by Item</CardTitle>
                  <CardDescription>Items with the biggest price movements</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sampleItems.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 * idx }}
                        className="flex items-center justify-between p-4 border border-card-border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="font-bold">{item.item}</h4>
                            <Badge variant="outline">{item.category}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>
                              Old: {insights.currency === 'USD' && '$'}
                              {insights.currency === 'EUR' && '€'}
                              {insights.currency === 'HKD' && 'HK$'}
                              {item.old_price.toFixed(2)}
                            </span>
                            <span>→</span>
                            <span>
                              New: {insights.currency === 'USD' && '$'}
                              {insights.currency === 'EUR' && '€'}
                              {insights.currency === 'HKD' && 'HK$'}
                              {item.new_price.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.change_pct > 0 ? (
                            <TrendingUp className="w-5 h-5 text-destructive" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-success" />
                          )}
                          <span className={`text-xl font-heading font-bold ${
                            item.change_pct > 0 ? 'text-destructive' : 'text-success'
                          }`}>
                            {item.change_pct > 0 ? '+' : ''}{item.change_pct.toFixed(1)}%
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <Card className="bg-card border-card-border">
                  <CardHeader>
                    <CardTitle>Biggest Increases</CardTitle>
                    <CardDescription>Items getting more expensive</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {sampleItems
                        .filter(item => item.change_pct > 0)
                        .slice(0, 5)
                        .map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-sm">{item.item}</span>
                            <span className="text-sm font-bold text-destructive">
                              +{item.change_pct.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      {sampleItems.filter(item => item.change_pct > 0).length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No price increases recorded
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Card className="bg-card border-card-border">
                  <CardHeader>
                    <CardTitle>Biggest Decreases</CardTitle>
                    <CardDescription>Items getting cheaper</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {sampleItems
                        .filter(item => item.change_pct < 0)
                        .slice(0, 5)
                        .map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-sm">{item.item}</span>
                            <span className="text-sm font-bold text-success">
                              {item.change_pct.toFixed(1)}%
                            </span>
                          </div>
                        ))}
                      {sampleItems.filter(item => item.change_pct < 0).length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No price decreases recorded
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InflationInsights;
